import React from "react";
import { Box, Typography, Chip, Button, IconButton, Tooltip, alpha } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

export type Kpi = {
  label: string;
  value: React.ReactNode;
  sub?: string;
  icon: React.ReactElement;
  // Pick a tone — drives the icon tile gradient and hover glow.
  tone?: "blue" | "purple" | "emerald" | "gold" | "red" | "cyan";
  trend?: { delta: string; positive?: boolean }; // e.g. { delta: "+12%", positive: true }
  urgent?: boolean;
};

export type ToolbarAction = {
  label: string;
  onClick: () => void;
  icon?: React.ReactElement;
  variant?: "primary" | "outline" | "danger";
  disabled?: boolean;
};

const TONE: Record<string, { gradient: string; glow: string }> = {
  blue:    { gradient: "linear-gradient(135deg, #3b82f6, #1d4ed8)", glow: "rgba(59,130,246,0.32)" },
  purple:  { gradient: "linear-gradient(135deg, #8b5cf6, #6d28d9)", glow: "rgba(139,92,246,0.32)" },
  emerald: { gradient: "linear-gradient(135deg, #10b981, #047857)", glow: "rgba(16,185,129,0.32)" },
  gold:    { gradient: "linear-gradient(135deg, #d4a017, #b8860b)", glow: "rgba(212,160,23,0.32)" },
  red:     { gradient: "linear-gradient(135deg, #ef4444, #b91c1c)", glow: "rgba(239,68,68,0.32)" },
  cyan:    { gradient: "linear-gradient(135deg, #06b6d4, #0e7490)", glow: "rgba(6,182,212,0.32)" },
};

interface Props {
  /** Module title — large header in the panel. */
  title: string;
  /** Subtitle text under the title. */
  subtitle?: string;
  /** Lucide-like icon to render inside the gradient circle. */
  icon: React.ReactElement;
  /** Optional small label chip in the corner — e.g. "AI" or "LIVE". */
  badge?: string;
  /** KPIs rendered as glass cards inside the gradient panel. */
  kpis?: Kpi[];
  /** Top-right toolbar action buttons. */
  actions?: ToolbarAction[];
  /** Refresh handler — renders a discrete refresh icon button. */
  onRefresh?: () => void;
  /** True when refresh is in flight. */
  refreshing?: boolean;
}

export const ProcurementPremiumHeader: React.FC<Props> = ({
  title, subtitle, icon, badge = "AI", kpis = [], actions = [], onRefresh, refreshing,
}) => (
  <Box sx={{
    position: "relative",
    p: 3, mb: 2.5, borderRadius: 3,
    background: "linear-gradient(135deg, #0B1E2D 0%, #1E293B 50%, #4C1D95 100%)",
    color: "#fff",
    boxShadow: "0 12px 32px rgba(11,30,45,0.20)",
    overflow: "hidden",
  }}>
    {/* Decorative orbs */}
    <Box sx={{ position: "absolute", top: -40, right: -40, width: 220, height: 220, borderRadius: "50%", background: "radial-gradient(circle, rgba(124,58,237,0.35) 0%, transparent 70%)", filter: "blur(40px)" }} />
    <Box sx={{ position: "absolute", bottom: -50, left: 100, width: 240, height: 240, borderRadius: "50%", background: "radial-gradient(circle, rgba(212,165,55,0.18) 0%, transparent 70%)", filter: "blur(50px)" }} />

    {/* Title row */}
    <Box sx={{ position: "relative", display: "flex", alignItems: "center", gap: 2, mb: kpis.length ? 2.5 : 0, flexWrap: "wrap" }}>
      <Box sx={{
        width: 52, height: 52, borderRadius: 3,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
        boxShadow: "0 8px 20px rgba(124,58,237,0.45)",
      }}>
        {React.cloneElement(icon, { sx: { fontSize: 28, color: "#fff" } })}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography fontSize={22} fontWeight={800} letterSpacing="-0.02em">{title}</Typography>
          {badge && (
            <Chip label={badge} size="small" sx={{
              height: 18, fontSize: 9, fontWeight: 800,
              bgcolor: alpha("#22d3ee", 0.18), color: "#22d3ee", letterSpacing: 0.6,
            }} />
          )}
        </Box>
        {subtitle && (
          <Typography fontSize={12} sx={{ color: "rgba(255,255,255,0.55)", mt: 0.25 }}>{subtitle}</Typography>
        )}
      </Box>

      {/* Right toolbar */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        {onRefresh && (
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={onRefresh} disabled={refreshing}
              sx={{
                width: 36, height: 36, borderRadius: 2,
                bgcolor: alpha("#fff", 0.08), color: "#fff",
                "&:hover": { bgcolor: alpha("#fff", 0.16) },
              }}>
              <RefreshIcon sx={{ fontSize: 18, animation: refreshing ? "spin 1s linear infinite" : "none", "@keyframes spin": { "100%": { transform: "rotate(360deg)" } } }} />
            </IconButton>
          </Tooltip>
        )}
        {actions.map((a, i) => {
          const isPrimary = (a.variant ?? "primary") === "primary";
          const isDanger  = a.variant === "danger";
          return (
            <Button key={i} size="small" disableElevation
              variant={isPrimary || isDanger ? "contained" : "outlined"}
              startIcon={a.icon}
              disabled={a.disabled}
              onClick={a.onClick}
              sx={{
                borderRadius: "10px", textTransform: "none", fontSize: 12, fontWeight: 700, px: 1.75,
                ...(isDanger
                  ? { background: "linear-gradient(135deg, #ef4444, #b91c1c)", color: "#fff", boxShadow: "0 6px 16px rgba(239,68,68,0.35)", "&:hover": { background: "linear-gradient(135deg, #dc2626, #991b1b)" } }
                  : isPrimary
                  ? { background: "linear-gradient(135deg, #d4a017, #b8860b)", color: "#0B1E2D", boxShadow: "0 6px 16px rgba(212,160,23,0.30)", "&:hover": { background: "linear-gradient(135deg, #b8860b, #92700a)" } }
                  : { borderColor: alpha("#fff", 0.20), color: "#fff", bgcolor: alpha("#fff", 0.06), "&:hover": { borderColor: alpha("#fff", 0.40), bgcolor: alpha("#fff", 0.12) } }),
              }}>
              {a.label}
            </Button>
          );
        })}
      </Box>
    </Box>

    {/* KPI cards */}
    {kpis.length > 0 && (
      <Box sx={{
        position: "relative",
        display: "grid",
        gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(2, 1fr)", md: `repeat(${Math.min(kpis.length, 6)}, 1fr)` },
        gap: 1.5,
      }}>
        {kpis.map((k, i) => {
          const tone = TONE[k.tone || "blue"];
          return (
            <Box key={i} sx={{
              p: 1.75, borderRadius: 2.5,
              bgcolor: alpha("#fff", 0.06),
              backdropFilter: "blur(8px)",
              border: `1px solid ${alpha("#fff", 0.08)}`,
              transition: "all 0.2s ease",
              "&:hover": { transform: "translateY(-2px)", bgcolor: alpha("#fff", 0.08), boxShadow: `0 8px 24px ${tone.glow}` },
            }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1 }}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: 2,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: tone.gradient, boxShadow: `0 6px 14px ${tone.glow}`,
                }}>
                  {React.cloneElement(k.icon, { sx: { fontSize: 20, color: "#fff" } })}
                </Box>
                {k.urgent && (
                  <Chip label="URGENT" size="small" sx={{ height: 16, fontSize: 8, fontWeight: 800, bgcolor: alpha("#ef4444", 0.18), color: "#fca5a5", letterSpacing: 0.6 }} />
                )}
                {!k.urgent && k.trend && (
                  <Chip label={k.trend.delta} size="small" sx={{
                    height: 16, fontSize: 8, fontWeight: 800, letterSpacing: 0.4,
                    bgcolor: alpha(k.trend.positive ? "#10b981" : "#ef4444", 0.16),
                    color: k.trend.positive ? "#86efac" : "#fca5a5",
                  }} />
                )}
              </Box>
              <Typography fontSize={10} fontWeight={700} sx={{ color: "rgba(255,255,255,0.55)", textTransform: "uppercase", letterSpacing: "0.08em" }}>{k.label}</Typography>
              <Typography fontSize={22} fontWeight={800} sx={{ color: "#fff", lineHeight: 1.2, fontFamily: "Georgia,'Times New Roman',serif" }}>{k.value}</Typography>
              {k.sub && <Typography fontSize={10} sx={{ color: "rgba(255,255,255,0.5)", mt: 0.25 }}>{k.sub}</Typography>}
            </Box>
          );
        })}
      </Box>
    )}
  </Box>
);

export default ProcurementPremiumHeader;
